import cn from "classnames";
import dayjs from "dayjs";
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useState } from "react";
import { BiPlus, BiX } from "react-icons/bi";
import { getCatalog } from "../../../../../api/catalog.service";
import Card from "../../../../../components/Card";
import Modal from "../../../../../components/Modal";
import { DEFAULT_DECK_AVATAR } from "../../../../../constants/images";
import styles from "./TopicContent.module.sass";

dayjs.extend(relativeTime);

const TopicContent = ({
  className,
  selectedItems,
  setSelectedItems
}) => {
  
  // Modal states
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  
  // Data states for table
  const [catalogTableData, setCatalogTableData] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [rawCatalogItems, setRawCatalogItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const isItemSelected = (item) => {
    const type = item.type;
    const itemId = (item.sourceId || item._id)?.toString();
    return selectedItems.some(s => {
      const sType = s.type;
      const sId = (s.sourceId || s._id)?.toString();
      return sType === type && sId && itemId && sId === itemId;
    });
  };

  const handleToggleItem = (item) => {
    if (!isItemSelected(item)) {
       const id = item.sourceId ?? item._id ?? item.id;
       if (!id) return;
       const itemToAdd = {
         ...item,
         sourceId: typeof id === 'string' ? id : String(id),
         type: item.type || 'deck',
       };
       setSelectedItems(prev => [...prev, itemToAdd]);
    }
  };

  const handleRemoveItem = (itemToRemove) => {
    const removeType = itemToRemove.type;
    const removeId = (itemToRemove.sourceId || itemToRemove._id)?.toString();
    setSelectedItems(prev => prev.filter(item => {
      const itemId = (item.sourceId || item._id)?.toString();
      return !(item.type === removeType && itemId === removeId);
    }));
  };

  // Format Catalog Items for Table
  // V3 API already returns CatalogItemDto with title (translated) and imageUrl (signed)
  // No need to extract or transform - just use the data directly
  useEffect(() => {
      setCatalogTableData(rawCatalogItems);
  }, [rawCatalogItems]);

  const fetchAndSetCatalog = async (searchTerm = '') => {
      setLoadingCatalog(true);
      try {
        const response = await getCatalog({
          take: 50,
          page: 0,
          title: searchTerm,
          available: true, // Show only available items
        });

        // Axios success: response = { data, status, ... }; on error getApi returns err (no .data.results)
        const ok = response && response.status >= 200 && response.status < 300;
        const data = ok ? response.data : null;

        // V3 API returns { pagination, results }
        if (data?.results && Array.isArray(data.results)) {
          setRawCatalogItems(data.results);
        } else if (data?.items && Array.isArray(data.items)) {
          setRawCatalogItems(data.items);
        } else if (Array.isArray(data)) {
          setRawCatalogItems(data);
        } else {
          if (!ok) console.warn('Catalog request failed or non-2xx:', response?.status, response?.data);
          setRawCatalogItems([]);
        }
      } catch (e) {
        console.error('Error fetching catalog:', e);
        setRawCatalogItems([]);
      } finally {
        setLoadingCatalog(false);
      }
  }

  useEffect(() => {
    if (isCatalogModalOpen) {
        fetchAndSetCatalog();
    }
  }, [isCatalogModalOpen]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchAndSetCatalog(value);
  };

  return (
    <>
      <Card className={cn(styles.card, className)}>
        <div className={styles.list}>
          
          <div className={styles.item}>
            <div className={styles.header}>
              <div className={styles.category}>Content (Decks & Texts)</div>
              <button 
                className={cn("button-small", styles.addButton)}
                onClick={() => setIsCatalogModalOpen(true)}
              >
                <BiPlus /> Add from Catalog
              </button>
            </div>

            {/* Selected Items List */}
            {selectedItems.length > 0 ? (
              <div className={styles.selectedItems}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Content</th>
                      <th>Type</th>
                      <th>Level</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItems.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <img
                            src={item.imageUrl || DEFAULT_DECK_AVATAR}
                            alt={item.title}
                            className={styles.thumbnail}
                            onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = DEFAULT_DECK_AVATAR;
                            }}
                          />
                        </td>
                        <td>{item.title || 'Untitled'}</td>
                        <td>{item.type}</td>
                        <td>{item.level || '-'}</td>
                        <td>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleRemoveItem(item)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
                <div className={styles.emptyState}>No content selected</div>
            )}
          </div>

        </div>
      </Card>

      {/* Catalog Modal */}
      <Modal
        visible={isCatalogModalOpen}
        onClose={() => setIsCatalogModalOpen(false)}
        outerClassName={styles.modalOuter}
      >
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h2>Select Content from Catalog</h2>
            <button 
              className={styles.closeButton}
              onClick={() => setIsCatalogModalOpen(false)}
            >
              <BiX size={24} />
            </button>
          </div>

          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search catalog..."
              value={searchTerm}
              onChange={handleSearch}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.catalogList}>
            {loadingCatalog ? (
                 <div className={styles.loadingState}>Loading catalog...</div>
             ) : catalogTableData.length === 0 ? (
                 <div className={styles.emptyState}>No items found</div>
             ) : (
                <table className={styles.catalogTable}>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Level</th>
                      <th>Count</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogTableData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <img
                            src={item.imageUrl || DEFAULT_DECK_AVATAR}
                            alt={item.title}
                            className={styles.thumbnail}
                            onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = DEFAULT_DECK_AVATAR;
                            }}
                          />
                        </td>
                        <td>
                          <div>
                            <div style={{fontWeight: 'bold'}}>{item.title}</div>
                            <div style={{fontSize: '12px', color: 'gray'}}>{item.type}</div>
                          </div>
                        </td>
                        <td>{item.level || '-'}</td>
                        <td>{item.contentCount ? `${item.contentCount} ${item.type === 'deck' ? 'cards' : 'words'}` : '-'}</td>
                        <td>
                          <button 
                            className={cn(styles.selectButton, isItemSelected(item) && styles.selected)}
                            onClick={() => handleToggleItem(item)}
                            disabled={isItemSelected(item)}
                          >
                            {isItemSelected(item) ? 'Selected' : 'Select'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             )}
          </div>

          <div className={styles.modalFooter}>
            <button 
              className={cn("button", styles.doneButton)}
              onClick={() => setIsCatalogModalOpen(false)}
            >
              Done ({selectedItems.length} selected)
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TopicContent;